<template>
  <ModalDialog
    ref="dialog"
    title="Edit Purchase Order"
    :submitting="submitting"
    submitTitle="Update"
    icon="fa-dolly-flatbed"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
      <FormRow label="Date Required">
        <FormDate v-model="order.dateRequired" field="dateRequired" />
      </FormRow>
      <FormRow label="Supplier Address">
        <FormSearchableSelect
          :options="contactAddressLookup"
          v-model="order.contactAddressId"
          field="contactAddressId"
          :optionSlot="true">
          <template v-slot:option="option">
            <div class="p-1">
              <LabelTags :labelList="option.data.deliveryLabelList" class="float-right" />
              {{option.data.name}}
            </div>                          
          </template>
        </FormSearchableSelect>
      </FormRow>
      <FormRow label="Receiving Location" helpTip="The location for this purchase order. All received goods will be assigned to this location">
        <AccountLocationSelect v-model="order.accountLocationId" field="accountLocationId" />
      </FormRow>
      <FormRow label="Despatch Method">
        <FormOption label="Delivery" name="despatchType" :setting="DespatchTypes.delivery" v-model="order.despatchType" inline="true" />
        <FormOption label="Pickup" name="despatchType" :setting="DespatchTypes.pickup" v-model="order.despatchType" inline="true" />
      </FormRow>
      <FormRow label="Notes">
        <FormInputTextArea v-model="order.notes" field="notes" placeholder="Supplier / Shipping Notes" large />
      </FormRow>
      <div v-show="order.despatchType == DespatchTypes.delivery">
        <FormHorizontalRule />
        <FormRow label="Delivery Location">
          <FormSelect :optionsPromise="accountLocationOptionsPromise" v-model="order.deliveryAccountLocationId" field="deliveryAccountLocationId" />
        </FormRow>

        <FormRow label="Delivery Address 1">
          <FormInputAddressSearch
            placeholder="Address 1"
            v-model="order.deliveryAddress1"
            field="deliveryAddress1"
            :readonly="!isAddressEditable"
            @updateAddress2="order.deliveryAddress2 = $event"
            @updateSuburb="order.deliverySuburb = $event"
            @updatePostcode="order.deliveryPostcode = $event"
            @updateState="order.deliveryState = $event"
            @updateCountry="order.deliveryCountry = $event"
          />
        </FormRow>
        <FormRow label="Delivery Address 2">
          <FormInputText placeholder="Address 2" v-model="order.deliveryAddress2" field="deliveryAddress2" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Suburb">
          <FormInputText placeholder="Suburb" v-model="order.deliverySuburb" field="deliverySuburb" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Postcode">
          <FormInputText placeholder="Postcode" v-model="order.deliveryPostcode" field="deliveryPostcode" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery State">
          <FormInputText placeholder="State" v-model="order.deliveryState" field="deliveryState" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Country">
          <FormInputText placeholder="Country" v-model="order.deliveryCountry" field="deliveryCountry" :readonly="!isAddressEditable" />
        </FormRow>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import { PurchaseOrderTypes, DespatchTypes } from '@/store/types'

export default {
  name: "EditPurchaseOrderDialog",
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    AccountLocationSelect
  },
  props: {
    purchaseOrderId: Number
  },
  data() {
    return {
      responseInfo: {},
      dialogForm: {},
      submitting: false,
      order: {},
      supplierData: {},
      accountData: {},
      accountLocationOptionsPromise: this.getAccountLocationOptionsPromise(),
    }
  },
  watch: {
    'order.deliveryAccountLocationId': function(newVal, oldVal) {
      // Fill in the delivery location details
      if(this.accountData.accountLocations) {
        var locationInfo = this.accountData.accountLocations.find(location => location.id == newVal)
        if(locationInfo) {
          this.order.deliveryAddress1 = locationInfo.address1
          this.order.deliveryAddress2 = locationInfo.address2
          this.order.deliverySuburb = locationInfo.suburb
          this.order.deliveryState = locationInfo.state
          this.order.deliveryPostcode = locationInfo.postcode
          this.order.deliveryCountry = locationInfo.country
        }
      }
    }
  },
  created() {
    this.PurchaseOrderTypes = PurchaseOrderTypes
    this.DespatchTypes = DespatchTypes
  },
  mounted() {
    
  },
  computed: {
    isAddressEditable() {
      return !this.order.deliveryAccountLocationId    // Only if custom address selected
    },
    contactAddressLookup() {
      var addressLookup = [{ name: 'None', value: null, labelList: [], deliveryLabelList: [] }]

      if(this.supplierData.addresses) {
        var addressLookup = _.concat(addressLookup, this.supplierData.addresses.map(address => {
          return {
            name: address.formattedAddress,
            value: address.id,
            labelList: address.labelList,
            deliveryLabelList: address.deliveryLabelList
          }
        }))
      }
      return addressLookup
    },
  },
  methods: {
    show(order) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.$data.order = _.cloneDeep(order)
      this.requestSupplierDetails()

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = {
        dateRequired: this.order.dateRequired,
        notes: this.order.notes,
        despatchType: this.order.despatchType,
        contactAddressId: this.order.contactAddressId,
        accountLocationId: this.order.accountLocationId,
        deliveryAccountLocationId: this.order.deliveryAccountLocationId,
        deliveryAddress1: this.order.deliveryAddress1,
        deliveryAddress2: this.order.deliveryAddress2,
        deliverySuburb: this.order.deliverySuburb,
        deliveryState: this.order.deliveryState,
        deliveryPostcode: this.order.deliveryPostcode,
        deliveryCountry: this.order.deliveryCountry,
      }

      this.$apiService.purchasingService.updatePurchaseOrder(this.purchaseOrderId, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    requestSupplierDetails() {
      this.$apiService.contactService.getContact(this.order.contactId)
      .then(result => {
        this.supplierData = result.data
      })
    },

    getAccountLocationOptionsPromise() {
      return new Promise((resolve, reject) => {
        this.$apiService.adminService.getAccount()
        .then(response => {
          let accountInfo = response.data

          this.accountData = accountInfo

          var locationOptions = this.accountData.accountLocations.map(location => {
            return { value: location.id, name: location.name }
          })
          return resolve(_.concat({ value: null, name: 'Custom' }, locationOptions))
        })
        .catch(err => { reject(err) })
      })
    },
  }
}
</script>